import { useState, useEffect, useMemo } from "react";
import IPermission, { PermissionHierarchyType } from "models/Permission.model";
import agent from "server";
import { AdminUserType } from "../types";

export interface UseAdminUsersReturn {
  users: AdminUserType[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook for fetching users in the admin console
 * Follows the exact same pattern as TeamList.tsx but for admin context
 * Can work for specific org or globally
 */
export const useGetUsers = ({
  orgId,
}: {
  orgId: string;
}): UseAdminUsersReturn => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<IPermission[]>([]);
  const [allUsers, setAllUsers] = useState<AdminUserType[]>([]);

  // Load permissions - exact same pattern as TeamList.tsx
  const loadPermissions = async (
    hierarchyType: PermissionHierarchyType,
    hierarchyId: string
  ) => {
    setLoading(true);
    setError(null);

    try {
      const res = await agent.Permissions.get(hierarchyType, hierarchyId);
      if (!res.success) {
        console.log("Unable to load users");
        setError(res.message || "Unable to load users");
        setPermissions([]);
      } else {
        console.log("Loaded users", res.data);
        setPermissions(res.data || []);
        setError(null);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Unknown error occurred";
      setError(errorMessage);
      console.error("Error in loadPermissions:", err);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  // Get all users from permissions - using admin API for user details
  const getAllUsers = async (
    userPromises: Promise<any>[],
    permissions: IPermission[]
  ) => {
    let allUsers: AdminUserType[] = [];
    try {
      const responses = await Promise.all(userPromises);

      for (let i = 0; i < responses.length; i++) {
        const response = responses[i];
        const permission = permissions[i];

        if (response.success && response.data) {
          const user = response.data;
          allUsers.push(user);
        } else {
          console.warn(
            `Failed to fetch user for permission ${permission.subject}`
          );
        }
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      allUsers = [];
    }
    setAllUsers(allUsers);
  };

  // Effect to load permissions when orgId changes - same as TeamList.tsx
  useEffect(() => {
    if (orgId) {
      loadPermissions(PermissionHierarchyType.Organization, orgId);
    }
  }, [orgId]);

  // Effect to get user details from permissions - using admin API
  useEffect(() => {
    if (permissions.length > 0) {
      const userPromises = permissions.map((permission) => {
        return agent.Admin.getUserAsAdmin(permission.subject);
      });
      getAllUsers(userPromises, permissions);
    } else {
      setAllUsers([]);
    }
  }, [permissions]);

  const refetch = async (): Promise<void> => {
    if (orgId) {
      await loadPermissions(PermissionHierarchyType.Organization, orgId);
    }
  };

  // Memoized users with admin-specific processing
  const processedUsers = useMemo(() => {
    return allUsers.map((user) => ({
      ...user,
    }));
  }, [allUsers]);

  return {
    users: processedUsers,
    loading,
    error,
    refetch,
  };
};

export default useGetUsers;
